import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { MASTER_KEYWORDS_TOP as MASTER_KEYWORDS_ALL } from "@/content/master-corpus/top.gen";
import type { BusinessFaqItem } from "@/content/business-corpus/faq-th";
import { BUSINESS_FAQ_CATEGORIES, getBusinessCategoryDef } from "@/content/business-corpus/faq-defs";

export const Route = createFileRoute("/business/faq/$category")({
  loader: async ({ params }) => {
    const { getBusinessFaqsByCategoryTh } = await import("@/content/business-corpus/categories");
    const def = getBusinessCategoryDef(params.category);
    if (!def) throw notFound();
    return { def, faqs: getBusinessFaqsByCategoryTh(def.slug) };
  },
  component: BusinessFaqCategoryPage,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "ไม่พบหมวดคำถาม" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { def, faqs } = loaderData;
    return {
      ...pageHeadForProvince({
        title: `${def.titleTh} — FAQ ธุรกิจ ${faqs.length} ข้อ (2026)`,
        description: def.descTh,
        path: `/business/faq/${params.category}`,
        type: "article",
        alternates: [{ lang: "en", path: `/en/business/faq/${params.category}` }],
        keywords: MASTER_KEYWORDS_ALL.slice(0, 60),
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "FAQ ธุรกิจ", url: `${site}/business/faq` },
              { name: def.titleTh, url: `${site}/business/faq/${params.category}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            inLanguage: "th",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/business/faq/${params.category}`)) },
      ],
    };
  },
});

function BusinessFaqCategoryPage() {
  const { def, faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/business/faq" className="hover:underline">FAQ ธุรกิจ</a> <span>/</span> <span>{def.titleTh}</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">{def.titleTh}</h1>
        <p className="text-muted-foreground mb-6">{def.descTh}</p>
        <p className="text-sm text-muted-foreground mb-8">
          <strong>{faqs.length}</strong> คำตอบในหมวดนี้ — เรียบเรียงโดยทีมทนายความ อัปเดตปี 2026
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-xs">
          {BUSINESS_FAQ_CATEGORIES.map((c) => (
            <a
              key={c.slug}
              href={`/business/faq/${c.slug}`}
              className={`rounded-full border px-3 py-1 ${c.slug === def.slug ? "bg-primary text-primary-foreground" : "bg-muted/40 text-muted-foreground hover:bg-muted"}`}
            >
              {c.titleTh}
            </a>
          ))}
        </div>

        <div className="space-y-4">
          {faqs.map((item: BusinessFaqItem, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>{item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
          {faqs.length === 0 && (
            <p className="rounded-lg border bg-muted/30 p-6 text-sm text-muted-foreground">
              ยังไม่มีคำตอบในหมวดนี้ — <a href="/contact" className="text-primary underline">สอบถามทีมงาน</a>
            </p>
          )}
        </div>
      
        <FaqSourceNote topic="business" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}